import { useEffect, useMemo, useRef } from 'react';
import debounce from 'lodash/debounce';
import { ONE_DAY } from '@atlassian/jira-portfolio-3-common';
import longTaskFactory from '@atlassian/jira-portfolio-3-common/src/long-task';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import {
	ANALYTICS_SCROLL_TIMELINE,
	ANALYITICS_TIMELINE_HORIZONTAL_SCROLLED,
	SPA_APP_NAME,
} from '../../common/constants';
import { useHorizontalScrolling, useTimelineRuler } from '../../controllers';

const longTask = longTaskFactory(SPA_APP_NAME);

/**
 * Provides a wrapper to instrument changes in the viewport
 * 1. Usage (what caused the scroll, how far, etc)
 * 2. Performance
 */
export default function HorizontalScrollAnalytics() {
	const [{ viewport, lastScrollTrigger, today }] = useHorizontalScrolling();
	const [{ msToPx }] = useTimelineRuler();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const lastReportedOffset = useRef<number | null>(null);

	const reportAnalytics = useMemo(
		() =>
			// eslint-disable-next-line @typescript-eslint/no-shadow
			debounce((trigger: string, newOffset: number, today: number) => {
				const [actionSubject, action] = ANALYTICS_SCROLL_TIMELINE.split(' ');
				const diffMs = newOffset - (lastReportedOffset.current ?? 0);

				fireUIAnalytics(createAnalyticsEvent({ action, actionSubject }), {
					trigger,
					distance: Math.ceil(msToPx(diffMs)),
					stopDate: Math.round((today + newOffset) / ONE_DAY) * ONE_DAY,
				});

				lastReportedOffset.current = newOffset;
			}, 1000),
		[createAnalyticsEvent, msToPx],
	);

	useEffect(() => {
		if (lastReportedOffset.current == null) {
			lastReportedOffset.current = viewport.offset;
		}

		if (lastScrollTrigger && lastReportedOffset.current !== viewport.offset) {
			reportAnalytics(lastScrollTrigger, viewport.offset, today);

			longTask.trackWithTimeout(ANALYITICS_TIMELINE_HORIZONTAL_SCROLLED, createAnalyticsEvent);
		}
	}, [lastScrollTrigger, viewport, today, reportAnalytics, createAnalyticsEvent]);

	return null;
}
